import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { X, GripVertical } from 'lucide-react';
import type {
  CRMUser,
  EnhancedDeal,
  ProjectOnboarding,
  ProjectTask,
  TaskGroup,
  TimberProject,
} from '../../types';
import { parseLegacyTitle } from '../../utils/projectName';
import { sortTimberProjectsForHubWorkQueue } from '../../utils/archEngineerWorkQueue';
import './ArchEngineerWorkQueueModal.css';

const VIEW_FOR_SESSION_KEY = 'pmArchEngWorkQueueViewForUid';

export interface ArchEngineerWorkQueueModalProps {
  open: boolean;
  onClose: () => void;
  /** Per-user ordered project ids from Firestore. */
  queuesByUserId: Record<string, string[]>;
  crmUsers: CRMUser[];
  /** Signed-in display name when the CRM row is missing (some portal logins). */
  viewerDisplayName?: string;
  timberProjects: TimberProject[];
  enhancedDeals: EnhancedDeal[];
  projectTasks: ProjectTask[];
  taskGroups: TaskGroup[];
  projectOnboardings: ProjectOnboarding[];
  currentUserId: string;
  canEditQueue: boolean;
  onSaveQueue: (subjectUserId: string, projectIds: string[]) => Promise<void>;
  onNavigateProject: (projectId: string) => void;
}

const ArchEngineerWorkQueueModal: React.FC<ArchEngineerWorkQueueModalProps> = ({
  open,
  onClose,
  queuesByUserId,
  crmUsers,
  viewerDisplayName = '',
  timberProjects,
  enhancedDeals,
  projectTasks,
  taskGroups,
  projectOnboardings,
  currentUserId,
  canEditQueue,
  onSaveQueue,
  onNavigateProject,
}) => {
  const queueSubjectOptions = useMemo(
    () =>
      crmUsers
        .filter((u) => u.isActive && (u.role === 'architect' || u.role === 'designer' || u.role === 'engineer'))
        .sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email)),
    [crmUsers],
  );

  const [subjectUserId, setSubjectUserId] = useState(currentUserId);
  const [localOrder, setLocalOrder] = useState<string[]>([]);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** Custom drag image clone; removed on drag end (HTML5 DnD otherwise previews only the handle). */
  const dragImageElRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    if (!canEditQueue) {
      setSubjectUserId(currentUserId);
      return;
    }
    try {
      const stored = sessionStorage.getItem(VIEW_FOR_SESSION_KEY);
      const list = queueSubjectOptions;
      const pick =
        stored && list.some((u) => u.uid === stored)
          ? stored
          : list[0]?.uid || currentUserId;
      setSubjectUserId(pick);
    } catch {
      setSubjectUserId(queueSubjectOptions[0]?.uid || currentUserId);
    }
  }, [open, canEditQueue, currentUserId, queueSubjectOptions]);

  const dealsById = useMemo(() => {
    const m = new Map<string, EnhancedDeal>();
    for (const d of enhancedDeals) {
      if (d.id) m.set(d.id, d);
    }
    return m;
  }, [enhancedDeals]);

  const queueIdsForSubject = useMemo(
    () => queuesByUserId[subjectUserId] ?? [],
    [queuesByUserId, subjectUserId],
  );

  const subjectProjectsSorted = useMemo(
    () =>
      sortTimberProjectsForHubWorkQueue(
        timberProjects,
        subjectUserId,
        projectTasks,
        taskGroups,
        projectOnboardings,
        queueIdsForSubject,
        dealsById,
      ),
    [timberProjects, subjectUserId, projectTasks, taskGroups, projectOnboardings, queueIdsForSubject, dealsById],
  );

  useEffect(() => {
    if (!open || !canEditQueue) return;
    setLocalOrder(subjectProjectsSorted.map((p) => p.id!));
  }, [open, canEditQueue, subjectUserId, subjectProjectsSorted]);

  const handleSubjectChange = useCallback(
    (uid: string) => {
      setSubjectUserId(uid);
      if (canEditQueue) {
        try {
          sessionStorage.setItem(VIEW_FOR_SESSION_KEY, uid);
        } catch {
          /* ignore */
        }
      }
    },
    [canEditQueue],
  );

  const subjectDisplayName = useMemo(() => {
    const row = crmUsers.find((u) => u.uid === subjectUserId);
    return (
      row?.displayName?.trim() ||
      row?.email ||
      (subjectUserId === currentUserId ? viewerDisplayName.trim() : '') ||
      'This person'
    );
  }, [crmUsers, subjectUserId, currentUserId, viewerDisplayName]);

  const clearDragImage = useCallback(() => {
    const el = dragImageElRef.current;
    if (el?.parentNode) {
      el.parentNode.removeChild(el);
    }
    dragImageElRef.current = null;
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent, id: string) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    setDraggingId(id);

    const handle = e.currentTarget as HTMLElement;
    const rowEl = handle.closest('.pm-ae-wq-row') as HTMLElement | null;
    if (!rowEl || !e.dataTransfer.setDragImage) return;

    const rect = rowEl.getBoundingClientRect();
    const clone = rowEl.cloneNode(true) as HTMLElement;
    clone.style.width = `${rect.width}px`;
    clone.style.boxSizing = 'border-box';
    clone.style.position = 'fixed';
    clone.style.left = '-9999px';
    clone.style.top = '0';
    clone.style.zIndex = '2147483647';
    clone.style.pointerEvents = 'none';
    clone.style.opacity = '0.98';
    clone.style.margin = '0';
    clone.classList.remove('pm-ae-wq-row--dragging');
    document.body.appendChild(clone);
    dragImageElRef.current = clone;

    const offsetX = Math.round(e.clientX - rect.left);
    const offsetY = Math.round(e.clientY - rect.top);
    try {
      e.dataTransfer.setDragImage(clone, offsetX, offsetY);
    } catch {
      clearDragImage();
    }
  }, [clearDragImage]);

  const handleDragEnd = useCallback(() => {
    clearDragImage();
    setDraggingId(null);
  }, [clearDragImage]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDropOnRow = useCallback(
    (e: React.DragEvent, targetId: string) => {
      e.preventDefault();
      const dragId = e.dataTransfer.getData('text/plain');
      clearDragImage();
      setDraggingId(null);
      if (!dragId || dragId === targetId) return;
      setLocalOrder((prev) => {
        const next = prev.filter((x) => x !== dragId);
        const idx = next.indexOf(targetId);
        if (idx === -1) return [...next, dragId];
        next.splice(idx, 0, dragId);
        return [...next];
      });
    },
    [clearDragImage],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSaveQueue(subjectUserId, localOrder);
      onClose();
    } finally {
      setSaving(false);
    }
  }, [localOrder, onSaveQueue, onClose, subjectUserId]);

  const rowLabel = useCallback(
    (p: TimberProject) => {
      const deal = p.dealId ? dealsById.get(p.dealId) : undefined;
      const num = (deal?.projectNumber || '').trim();
      const numberDisplay = num ? (num.startsWith('#') ? num : `#${num}`) : '';
      const descriptor = (
        (deal?.descriptor || '').trim() ||
        parseLegacyTitle(deal?.title || '').descriptor ||
        p.name ||
        ''
      ).trim();
      return { numberDisplay, descriptor: descriptor || p.name || 'Project' };
    },
    [dealsById],
  );

  useEffect(() => {
    if (!open) clearDragImage();
  }, [open, clearDragImage]);

  useEffect(() => () => clearDragImage(), [clearDragImage]);

  if (!open) return null;

  const rows = canEditQueue
    ? localOrder
        .map((id) => timberProjects.find((p) => p.id === id))
        .filter((p): p is TimberProject => !!p)
    : subjectProjectsSorted;

  const showViewForControl = canEditQueue && queueSubjectOptions.length > 0;

  return (
    <div className="pm-delete-overlay" role="presentation" onClick={onClose}>
      <div
        className="pm-ae-wq-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pm-ae-wq-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pm-ae-wq-modal-header">
          <div className="pm-ae-wq-modal-header-text">
            <h2 id="pm-ae-wq-title" className="pm-ae-wq-modal-title">
              Work queue
            </h2>
            {showViewForControl ? (
              <label className="pm-ae-wq-view-for">
                <span className="pm-ae-wq-view-for-label">View for</span>
                <select
                  className="pm-ae-wq-view-for-select"
                  value={subjectUserId}
                  onChange={(e) => handleSubjectChange(e.target.value)}
                >
                  {queueSubjectOptions.map((u) => (
                    <option key={u.uid} value={u.uid}>
                      {u.displayName || u.email}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              !canEditQueue && (
                <p className="pm-ae-wq-view-for-readonly">
                  <span className="pm-ae-wq-view-for-label">View for</span> {subjectDisplayName}
                </p>
              )
            )}
            <p className="pm-ae-wq-modal-sub">
              {canEditQueue
                ? `Drag rows to set project priority for ${subjectDisplayName}. Only projects where they have active assigned or action-required tasks are listed.`
                : 'Projects where you have active assigned or action-required tasks, in the order your project manager set for you.'}
            </p>
          </div>
          <button type="button" className="pm-ae-wq-close" onClick={onClose} aria-label="Close">
            <X size={18} strokeWidth={2} aria-hidden />
          </button>
        </div>

        <div className="pm-ae-wq-list" role="list">
          {canEditQueue && queueSubjectOptions.length === 0 ? (
            <p className="pm-ae-wq-empty">
              No active designer or engineer profiles in the team list. Add CRM users with role Designer or
              Engineer to use per-person work queues.
            </p>
          ) : rows.length === 0 ? (
            <p className="pm-ae-wq-empty">
              {canEditQueue
                ? `No active tasks involving ${subjectDisplayName} right now.`
                : 'No active tasks involving you right now.'}
            </p>
          ) : (
            rows.map((p, i) => {
              const { numberDisplay, descriptor } = rowLabel(p);
              const pid = p.id!;
              if (!canEditQueue) {
                return (
                  <button
                    key={pid}
                    type="button"
                    className="pm-ae-wq-row pm-ae-wq-row--clickable"
                    onClick={() => {
                      onNavigateProject(pid);
                      onClose();
                    }}
                  >
                    <span className="pm-ae-wq-rank">{i + 1}</span>
                    <span className="pm-ae-wq-row-main">
                      {numberDisplay && <span className="pm-ae-wq-num">{numberDisplay}</span>}
                      <span className="pm-ae-wq-desc">{descriptor}</span>
                    </span>
                  </button>
                );
              }
              return (
                <div
                  key={pid}
                  role="listitem"
                  className={`pm-ae-wq-row pm-ae-wq-row--drag ${draggingId === pid ? 'pm-ae-wq-row--dragging' : ''}`}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDropOnRow(e, pid)}
                >
                  <span
                    className="pm-ae-wq-drag"
                    draggable
                    onDragStart={(e) => handleDragStart(e, pid)}
                    onDragEnd={handleDragEnd}
                    aria-label={`Drag to reorder: ${descriptor}`}
                  >
                    <GripVertical size={16} strokeWidth={2} aria-hidden />
                  </span>
                  <span className="pm-ae-wq-rank">{i + 1}</span>
                  <button
                    type="button"
                    className="pm-ae-wq-row-main"
                    onClick={() => {
                      onNavigateProject(pid);
                      onClose();
                    }}
                  >
                    {numberDisplay && <span className="pm-ae-wq-num">{numberDisplay}</span>}
                    <span className="pm-ae-wq-desc">{descriptor}</span>
                  </button>
                </div>
              );
            })
          )}
        </div>

        {canEditQueue && queueSubjectOptions.length > 0 && rows.length > 0 && (
          <div className="pm-ae-wq-footer">
            <button type="button" className="pm-btn pm-btn-ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="pm-btn pm-btn-primary" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Saving…' : 'Save order'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchEngineerWorkQueueModal;
